<template>
  <div class="product-form">
    <slot name="banner" />
    <Card tag="form" v-bind="$attrs" class="product-form__card" @submit.prevent="submit">
      <template #header>
        <div>
          <div class="product-form__card__header">
            <div class="product-form__card__title">
              <h3>{{ $t('about', { x: $tc('product', 1).toLowerCase() }) }}</h3>
              <Badge
                :theme="isScoped ? 'warning' : 'success'"
                :icon="`fa-pro-solid:${isScoped ? 'link-slash' : 'link'}`"
                :value="$t(isScoped ? 'scopedProduct' : 'globalProduct')"
                style="cursor: default"
                v-tooltip="$t(`${isScoped ? 'scoped' : 'global'}ProductExplanation`)"
              />
            </div>
            <InputComponent
              v-if="isEnabled('internalId')"
              :value="getValue('internalId')"
              :disabled="!('internalId' in value) || Boolean(getDisabledField('internalId'))"
              v-tooltip.bottom="getDisabledField('internalId')"
              @input="$ev => setValue('internalId', $ev)"
              data-field="internalId"
              :label="`${labels.internalId}:`"
              :placeholder="$t('typeHere') + '...'"
            />
          </div>
          <div class="product-form__card__info">
            <InputComponent
              v-if="isEnabled('name')"
              :value="getValue('name')"
              :disabled="!('name' in value) || Boolean(getDisabledField('name'))"
              v-tooltip.bottom="getDisabledField('name')"
              @input="$ev => setValue('name', $ev)"
              data-field="name"
              :label="labels.name"
              required
              :is-valid="getValidation('name').valid"
              :validation-message="getValidation('name').error"
            />
            <InputComponent
              data-field="category"
              v-if="isEnabled('category')"
              :label="labels.category"
              :disabled="!('category' in value) || Boolean(getDisabledField('category'))"
              v-tooltip.bottom="getDisabledField('category')"
              :is-valid="getValidation('category').valid"
              required
            >
              <VirtualCategory
                items="/api/product/categories"
                :mode="$path('settings.product.category.mode', vendor)"
                :value="getValue('category')"
                @input="$ev => setValue('category', $ev)"
              />
            </InputComponent>
            <InputComponent
              v-if="isEnabled('supplier')"
              :value="getValue('supplier')"
              :disabled="!('supplier' in value) || Boolean(getDisabledField('supplier'))"
              v-tooltip.bottom="getDisabledField('supplier')"
              @input="$ev => setValue('supplier', $ev)"
              data-field="supplier"
              :placeholder="$t('typeHere') + '...'"
              :label="labels.supplier"
              :is-valid="getValidation('supplier').valid"
              :validation-message="getValidation('supplier').error"
            />
            <Tags
              data-field="tags"
              :label="$tc('tag', 2)"
              :disabled="!('tags' in value) || Boolean(getDisabledField('tags'))"
              v-tooltip.bottom="getDisabledField('tags')"
              :value="getValue('tags')"
              @input="$ev => setValue('tags', $ev)"
              :placeholder="$tc('add')"
              autocomplete="/api/product/tags"
            />
            <Tabs
              v-model="current"
              class="product-form__card__descriptions"
              :items="[
                {
                  id: 'description',
                  label: $t('description'),
                  disabled: !isEnabled('description') || !('description' in value),
                },
                {
                  id: 'technicalDescription',
                  label: $t('technicalDescription'),
                  disabled: !isEnabled('technicalDescription') || !('technicalDescription' in value),
                },
              ]"
              theme="border"
            >
              <template #description>
                <div class="tabs__tab">
                  <RichTextComponent
                    :value="getValue('description')"
                    :disabled="!('description' in value) || Boolean(getDisabledField('description'))"
                    v-tooltip.bottom="getDisabledField('description')"
                    @input="$ev => setValue('description', $ev)"
                  />
                </div>
              </template>
              <template #technicalDescription>
                <div class="tabs__tab">
                  <RichTextComponent
                    :value="getValue('technicalDescription')"
                    :disabled="!('technicalDescription' in value) || Boolean(getDisabledField('technicalDescription'))"
                    v-tooltip.bottom="getDisabledField('technicalDescription')"
                    @input="$ev => setValue('technicalDescription', $ev)"
                  />
                </div>
              </template>
            </Tabs>
            <MediaComponent
              :label="$tc('image', 2)"
              v-if="isEnabled('images')"
              :value="getValue('images')"
              :disabled="!('images' in value) || Boolean(getDisabledField('images'))"
              v-tooltip.bottom="getDisabledField('images')"
              @input="$ev => setValue('images', $ev)"
              :upload-options="{ compression: { maxWidth: 1920 } }"
              :upload="upload"
              accept="image/*,.pdf"
              placement="inside"
              size="contain"
              multiple
              class="grow-1"
              data-field="images"
              :enableDescription="true"
              :tags="true"
            />

            <div class="col g-1">
              <MediaComponent
                :label="$tc('thumbnail', 1)"
                v-if="isEnabled('thumbnail')"
                :value="getValue('thumbnail')"
                :disabled="!('thumbnail' in value) || Boolean(getDisabledField('thumbnail'))"
                v-tooltip.bottom="getDisabledField('thumbnail')"
                @input="$ev => setValue('thumbnail', $ev)"
                :upload-options="{ compression: { maxWidth: 500 } }"
                :upload="upload"
                accept="image/*"
                placement="inside"
                size="contain"
                data-field="thumbnail"
                :enableDescription="false"
                :enableAlt="false"
              />
              <FileComponent
                v-if="isEnabled('attachments')"
                v-model="attachmentComp"
                data-field="attachments"
                multiple
                :label="labels.attachments"
                :disabled="Boolean(getDisabledField('attachments'))"
                v-tooltip.bottom="getDisabledField('attachments')"
              />
            </div>
          </div>
          <CustomFields
            v-if="customFieldSchemas.length"
            data-field="customFields"
            v-model="data.customFields"
            :schema="customFieldSchemas"
            :data="data"
            :validator="validator"
            class="mt-2"
            validator-prefix="customFields"
            :placement="['product']"
          />
        </div>
      </template>
      <template #default>
        <div class="product-form__card__price">
          <h3 class="product-form__card__title">{{ $t('price') }}</h3>
          <Overrider
            :value="Boolean(data.pricing)"
            :label="$t('price')"
            :active="Boolean(template)"
            @override="() => override('pricing')"
            @reset="() => resetOverride('pricing')"
          >
            <Tabs
              v-if="isEnabled('pricing')"
              :value="getValue('pricing.type')"
              @input="$ev => setValue('pricing.type', $ev)"
              :items="[
                {
                  id: 'fixed',
                  label: $t('fixedPrice'),
                },
                {
                  id: 'area',
                  label: $t('areaPrice'),
                },
                {
                  id: 'request',
                  label: $t('priceOnRequest'),
                },
              ]"
              theme="border"
            >
              <template #before>
                <div class="tabs__tab" v-if="getValue('pricing.type') === 'request'">
                  {{ $t('priceOnRequest') }}
                </div>
                <div class="tabs__tab" v-else>
                  <InputComponent
                    :disabled="Boolean(getDisabledField('pricing.value'))"
                    v-tooltip.bottom="getDisabledField('pricing.value')"
                    :value="getValue('pricing.value')"
                    @input="$ev => setValue('pricing.value', $ev, 'number')"
                    :suffix="$store.state.enums.Region.currency"
                    :mask="{
                      mask: Number,
                      unmask: true,
                      thousandsSeparator: ' ',
                    }"
                    :required="isRequired('pricing.value')"
                    :label="labels['pricing.value'] + ` (${$t('excludingVat')})`"
                    :is-valid="getValidation('pricing.value').valid"
                    :validation-message="getValidation('pricing.value').error"
                  />
                  <InputComponent
                    :disabled="Boolean(getDisabledField('pricing.margin.value'))"
                    v-tooltip.bottom="getDisabledField('pricing.margin.value')"
                    :value="getValue('pricing.margin.value')"
                    @input="$ev => setValue('pricing.margin.value', $ev, 'number')"
                    :required="isRequired('pricing.margin.value')"
                    :mask="{
                      mask: Number,
                      unmask: true,
                      thousandsSeparator: ' ',
                    }"
                    :label="
                      labels['pricing.margin.value'] +
                      (getValue('pricing.margin.type') === 'fixed' ? ` (${$t('excludingVat')})` : '')
                    "
                    :is-valid="getValidation('pricing.margin.value').valid"
                    :validation-message="getValidation('pricing.margin.value').error"
                  >
                    <template #right>
                      <ChoiceComponent
                        :disabled="Boolean(getDisabledField('pricing.margin.type'))"
                        v-tooltip.bottom="getDisabledField('pricing.margin.type')"
                        :value="getValue('pricing.margin.type')"
                        @input="$ev => setValue('pricing.margin.type', $ev)"
                        class="product-form__card__margin-type"
                        :values="marginTypes"
                      />
                    </template>
                  </InputComponent>
                  <InputComponent
                    v-if="vatValues.length"
                    :label="$t('valueaddedtaxLabel')"
                    data-field="pricing.vat"
                    :disabled="Boolean(getDisabledField('pricing.vat'))"
                    v-tooltip.bottom="getDisabledField('pricing.vat')"
                  >
                    <template #default>
                      <Radio
                        :disabled="Boolean(getDisabledField('pricing.vat'))"
                        :value="getValue('pricing.vat')"
                        @input="$ev => setValue('pricing.vat', $ev)"
                        :items="vatValues"
                      />
                    </template>
                  </InputComponent>
                  <InputComponent
                    :label="$t('priceRounding')"
                    data-field="pricing.rounding"
                    :disabled="Boolean(getDisabledField('pricing.rounding'))"
                    v-tooltip.bottom="getDisabledField('pricing.rounding')"
                  >
                    <template #default>
                      <Radio
                        :disabled="Boolean(getDisabledField('pricing.rounding'))"
                        :value="getValue('pricing.rounding')"
                        @input="$ev => setValue('pricing.rounding', $ev)"
                        :items="roundingTypes"
                      />
                    </template>
                  </InputComponent>
                  <InputComponent
                    :label="$t('price')"
                    data-field="pricing.pricing"
                    :disabled="Boolean(getDisabledField('pricing.pricing'))"
                    v-tooltip.bottom="getDisabledField('pricing.pricing')"
                  >
                    <template #default>
                      <Price
                        :value="getValue('pricing')"
                        v-bind="priceBinding"
                        :disabled="Boolean(getDisabledField('pricing.pricing'))"
                      />
                    </template>
                  </InputComponent>
                </div>
              </template>
            </Tabs>
          </Overrider>
          <CustomFields
            v-if="customFieldSchemas.length"
            data-field="customFields"
            v-model="data.customFields"
            :schema="customFieldSchemas"
            :data="data"
            :validator="validator"
            validator-prefix="customFields"
            class="mt-2"
            :placement="['product:pricing']"
          />
        </div>
        <div class="product-form__card__additions">
          <h3 class="product-form__card__title">
            {{ $t('productOptionsLabel') }}
            <HelperPopover template="product-options" />
          </h3>
          <Bundle
            :value="getValue('products')"
            @input="$ev => setValue('products', $ev)"
            :disabled="!('products' in value) || Boolean(getDisabledField('products'))"
            @track="track"
            v-tooltip.bottom="getDisabledField('products')"
          />
          <InputComponent
            class="product-form__card__additions__wrapper"
            :disabled="!('variants' in value) || Boolean(getDisabledField('variants'))"
            v-tooltip.bottom="getDisabledField('variants')"
          >
            <div class="elder-input__element">
              <ProductVariant
                :id="value.id"
                :value="getValue('variants')"
                @input="$ev => setValue('variants', $ev)"
                @track="track"
              />
            </div>
          </InputComponent>
        </div>
      </template>
      <template #footer>
        <slot name="footer" :validator="validator" />
      </template>
    </Card>
  </div>
</template>

<script>
import { mapi18n } from '@/i18n'
import { mapState } from 'vuex'
import { Clone, setByPath, hasDiff } from '@/utils'
import Validator from '@/mixins/validator'
import PricingModel from '@/models/Shop/pricing'
import Overrider from './Overrider'
import CustomFields, { getValidatorConfig } from '@kvass/custom-fields'

import { VueComponentUpload as upload } from '@kvass/storage'

import Radio from '@kvass/radio'

import { InputComponent } from 'vue-elder-input'
import { ButtonComponent as Button } from 'vue-elder-button'
import { FileComponent } from '@kvass/file'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { ChoiceComponent } from 'vue-elder-choice'
import Bundle from './Bundle.vue'
import Tabs from '@/components/Tabs'
import { MediaComponent } from '@kvass/media'
import RichTextComponent from '@kvass/rich-text'
import Price from './Price'
import Card from '@/components/Card'
import Tags from '@kvass/custom-fields/src/Fields/Tags'
import VirtualCategory from '@/components/VirtualCategory'
import HelperPopover from '@/components/HelperPopover/Component'
import ProductSelect from '@/components/Product/Select/Modal'
import ProductVariant from '@/components/Product/Select/Variant'
import { AlertComponent as Alert } from 'vue-elder-alert'
import Badge from '@/components/Badge'
import tracker from '@/tracking'

const labels = mapi18n([
  'internalId',
  'name',
  'priceOnRequest',
  'description',
  'technicalDescription',
  'productBundle',
  'supplier',
  { 'pricing.value': 'costPrice' },
  { 'pricing.margin.value': 'priceMargin' },
  { category: ['category', 1] },
  { attachments: ['attachment', 2] },
  { thumbnail: ['thumbnail', 2] },
  'variants',
])

const Fields = [
  'images',
  'thumbnail',
  'name',
  'category',
  'description',
  'technicalDescription',
  'products',
  'supplier',
  'pricing',
  'attachments',
  'internalId',
  'variants',
]

export { labels }

export default {
  mixins: [Validator],
  props: {
    value: Object,
    template: Object,

    rules: {
      type: Object,
      default: () => ({
        name: 'required',
        category: 'required',
        'pricing.value': 'required_unless:pricing.type,request|min:0',
        'pricing.margin.value': 'required_unless:pricing.type,request|min:0',
      }),
    },
    fields: {
      type: Array,
      default: () => Fields,
    },
    disabledFields: {
      type: Object,
      default: () => ({}),
    },
    area: {
      type: Number,
      default: 50,
    },
  },
  watch: {
    value: {
      handler(val) {
        if (!val) return

        if (!hasDiff(this.data, Clone(val))) return

        this.data = Clone(val)
      },
      immediate: true,
    },
    'data.pricing.type': {
      handler(val) {
        this.track('Pricing')

        if (val !== 'request' || !this.data.pricing) return

        this.data.pricing.value = this.data.pricing.value || 0
        this.data.pricing.margin.value = this.data.pricing.margin.value || 0
      },
    },
    'data.pricing.rounding': {
      handler(val) {
        this.track('PricingRounding')
      },
    },
    'data.pricing.margin.type': {
      handler(val) {
        this.track('PricingType')
      },
    },
    'data.customFields': {
      handler(val, oldVal) {
        this.$emit('input', this.data)
      },
      deep: true,
    },
  },
  created() {
    this.fetchSchema()
  },
  data() {
    return {
      data: {},
      current: 'description',
      promise: null,
      isBundleState: false,
      schemas: [],
      validator: {
        rules: 'mergedRules',
        data: 'dataComputed',
        labels: 'labels',
      },
      marginTypes: [
        { value: 'fixed', label: this.$store.state.enums.Region.currency },
        { value: 'percent', label: '%' },
      ],
      roundingTypes: [
        { value: 'none', label: this.$t('rounding:none') },
        { value: '50', label: '~50' },
        { value: '100', label: '~100' },
      ],
    }
  },
  computed: {
    ...mapState(['vendor']),
    ...mapState('Product', ['states']),
    vatValues() {
      const vat = this.$store.state.enums.Region.vat
      if (!vat.editable) return []
      return vat.values.map(value => ({ value, label: `${value}%` }))
    },
    isBundle: {
      get() {
        return Boolean(this.$path('value.products.length')) || this.isBundleState
      },
    },
    isScoped() {
      return Boolean(this.$path('value.reference.ref'))
    },
    priceBinding() {
      if (this.$options.propsData.area) return { area: this.area }
      return {}
    },
    customFieldSchemas() {
      if (this.states.fetchOne) return []
      if (!this.schemas.length) return []
      return this.schemas.flatMap(c => c.schema || c)
    },
    customFieldValidatorConfig() {
      if (!this.customFieldSchemas.length) return {}
      return getValidatorConfig(this.customFieldSchemas, this.data, { prefix: 'customFields' })
    },
    labels() {
      return {
        ...labels,
        ...this.customFieldValidatorConfig.labels,
      }
    },
    mergedRules() {
      return {
        ...this.rules,
        ...this.customFieldValidatorConfig.rules,
      }
    },
    dataComputed() {
      return {
        ...this.data,
        pricing: this.data.pricing || this.template.pricing,
      }
    },
    attachmentComp: {
      get() {
        return this.getValue('attachments')
      },
      set(val) {
        return this.setValue('attachments', val)
      },
    },
  },
  methods: {
    fetchSchema() {
      const id = this.value && this.value.id
      const url = new URL('/api/specs/customfieldschema/product', window.location.origin)
      if (id) url.searchParams.set('id', id)
      return fetch(url)
        .then(res => res.json())
        .then(data => (this.schemas = data))
    },
    track(v) {
      tracker.emit(`${v}:product`)
    },
    override(key) {
      switch (key) {
        case 'pricing':
          this.$set(this.data, key, PricingModel(this.template[key]))
      }
    },
    isRequired(key) {
      return Boolean(this.rules && this.rules[key] && this.rules[key].match(/required/))
    },
    resetOverride(key) {
      switch (key) {
        case 'pricing':
          this.$set(this.data, key, null)
      }
    },
    getDisabledField(field) {
      return (this.disabledFields || {})[field]
    },
    isEnabled(field) {
      let includes = this.fields.filter(f => !f.startsWith('!'))
      let excludes = this.fields.filter(f => f.startsWith('!')).map(f => f.substring(1))

      if (includes.length) return includes.includes(field)
      if (excludes.length) return !excludes.includes(field)

      return true
    },
    getValidation(key) {
      let target = this.$path(`validator.fields`)[key]
      if (!target) return { valid: true, error: undefined }
      return target
    },
    getValue(key) {
      let mainKey = key.split('.')[0]

      if (![null, undefined].includes(this.data[mainKey])) return this.$path('data.' + key)
      return this.$path('template.' + key)
    },
    setValue(key, value, type) {
      switch (key) {
        case 'products': {
          if (value.length) {
            this.isBundleState = true
          } else {
            this.isBundleState = false
            this.$emit('input', {
              ...this.value,
              products: [],
            })
          }
          break
        }
      }
      switch (type) {
        case 'number':
          let parsed = parseInt(value)
          setByPath(key, isNaN(parsed) ? null : parsed, this.data)
          break
        default:
          setByPath(key, value, this.data)
          break
      }

      return this.$emit('input', this.data)
    },

    submit() {
      this.$emit('input', this.data)
      this.$emit('submit', this.data)
    },
    async upload(file, onProgress, options = {}) {
      if (file.type === 'application/pdf') options.convert = '/api/file/transform/png?scale=2'
      return upload(file, onProgress, options)
    },
  },
  components: {
    Alert,
    Button,
    Card,
    InputComponent,
    FileComponent,
    RichTextComponent,
    CheckboxComponent,
    ChoiceComponent,
    Price,
    Tabs,
    Overrider,
    Tags,
    VirtualCategory,
    MediaComponent,
    ProductSelect,
    ProductVariant,
    HelperPopover,
    Radio,
    Bundle,
    Badge,
    CustomFields,
  },
}
</script>

<style lang="scss">
.product-form {
  margin: 0 auto;
  max-width: 1000px;
}

.product-form__card {
  .card__header,
  .card__content {
    padding: 2.5rem;
  }

  .card__header {
    align-items: initial;
    border-bottom: none;
  }

  .card__footer {
    grid-column-end: span 2;
  }

  .elder-input[disabled='disabled'],
  .kvass-media[disabled='disabled'],
  .kvass-file[readonly='readonly'] {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
    transition: opacity 150ms ease-out;
  }

  .tabs {
    margin-top: 1rem;
  }

  &__bundle-select {
    .product-card__subtitle {
      display: none;
    }
  }

  h3 {
    font-weight: 700;
    font-size: 1.6rem;
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;

    .trigger {
      display: flex !important;
      align-self: center;
    }
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  &__descriptions {
    .tabs__tab {
      padding: 0 !important;

      .richtext {
        border: none !important;
        position: relative;
        border-top-left-radius: 0 !important;

        &__menubar {
          padding: 1rem;
        }
      }
    }
  }

  &__info,
  &__additions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: math.div($form-gap-row, 2) $form-gap-column;
    align-items: flex-start;
  }

  &__header {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: math.div($form-gap-row, 2) $form-gap-column;
    margin-bottom: 2rem;
    width: 100%;

    [data-field='internalId'] {
      flex-direction: row;
      flex-grow: 1;
      align-items: center;
      padding: 0.3rem 0.8rem;
      background-color: #eaeaea;
      border-radius: 0.5rem;
      width: 100%;

      .elder-input {
        &__label {
          display: block;
          font-size: 0.9rem;
        }

        &__wrapper {
          margin-top: 0;
          align-items: center;
        }

        &__field {
          border: 1px solid transparent;
          background-color: transparent;
          padding: 0 0.2rem;
          font-size: 1rem;
          font-weight: 700;
          color: var(--dark);

          &--focus {
            border: 1px solid var(--vue-elder-primary, #3a9acd);
          }
        }
      }
    }
  }

  &__info {
    grid-template-columns: 1fr 1fr;
    grid-gap: math.div($form-gap-row, 2) $form-gap-column;
    align-items: flex-start;
    padding-top: 0;

    .tabs {
      grid-column: span 2;
    }

    [data-field='category'] {
      cursor: pointer;
      overflow-x: auto;
    }

    [data-field='description'],
    [data-field='technicalDescription'] {
      height: 100%;
    }

    [data-field='images'] {
      min-height: 450px;
      height: 100%;

      .kvass-media-droparea {
        background-color: white;
      }
    }

    [data-field='attachments'] {
      min-height: 300px;
      height: 100%;

      .kvass-file__droparea {
        background-color: white;
      }
    }

    [data-id='description'],
    [data-id='technicalDescription'] {
      padding: 0;

      .tabs__tab {
        padding: 1.4rem 1.3rem;
      }

      .richtext {
        background-color: white;
        border-radius: 0.5rem;
        border: 1px solid var(--border-color);
      }
    }
  }

  &__price {
    .tabs__tab {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: math.div($form-gap-row, 2) $form-gap-column;
    }

    [data-field='pricing.rounding'],
    [data-field='pricing.vat'] {
      height: auto;
      align-self: start;

      .kvass-radio {
        justify-content: space-around;
        padding: 1rem 0;
      }
    }

    [data-field='pricing.pricing'] {
      .product-price {
        margin-top: 0;
        border: none;
        background-color: #fafafa;
      }
    }

    &:has([data-field='pricing.vat']) [data-field='pricing.pricing'] {
      grid-column-end: span 2;
    }
  }

  &__additions {
    margin-top: var(--card-spacing);

    h3 {
      grid-column: span 2;
    }

    .variants-modal,
    .product-bundle {
      .elder-input {
        &__element {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      &__default {
        flex-direction: column;
        width: 100%;

        min-height: 250px;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;

        &:hover {
          cursor: pointer;
        }

        h3 {
          font-size: 1.4rem;
          margin-bottom: 0;
        }

        p {
          margin-top: 0.2rem;
        }

        &__items {
          list-style: none;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
          width: 100%;

          margin: 0;
          padding: 0 1rem;

          &--element:nth-of-type(n + 4) {
            display: none;
          }
        }
      }
    }
  }

  &__margin-type {
    @include gap(0.25rem);
    flex-wrap: nowrap;

    .elder-choices__item-label {
      padding: 1rem;
      min-width: 80px;
    }
  }
}
</style>
